import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Clock, Flag, ChevronLeft, ChevronRight, Send, AlertTriangle } from 'lucide-react';
import { toast } from 'react-hot-toast';
import api from '../../utils/api';

interface Question {
  id: string;
  title: string;
  content?: string;
  type: string;
  points: number;
  timeLimit: number;
  options?: string[];
}

interface ExamData {
  id: string;
  title: string;
  description?: string;
  duration: number;
  totalScore: number;
  totalPoints: number;
  instructions?: string;
  settings?: {
    shuffleQuestions?: boolean;
    shuffleOptions?: boolean;
    timeLimit?: boolean;
  };
  questions: Question[];
}

interface SessionData {
  id: string;
  attemptNumber: number;
  startedAt: string;
  remainingTime: number;
}

const ExamTaking: React.FC = () => {
  const { examId } = useParams<{ examId: string }>();
  const navigate = useNavigate();
  
  const [examData, setExamData] = useState<ExamData | null>(null);
  const [sessionData, setSessionData] = useState<SessionData | null>(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, any>>({});
  const [markedForReview, setMarkedForReview] = useState<Set<string>>(new Set());
  const [timeLeft, setTimeLeft] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (examId) {
      startExam();
    }
  }, [examId]);

  useEffect(() => {
    if (timeLeft > 0 && !submitting) {
      const timer = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            // 时间到，自动提交
            handleSubmitExam();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [timeLeft, submitting]);

  const startExam = async () => {
    if (!examId) {
      setError('考试ID无效');
      setLoading(false);
      return;
    }

    try {
      // 先尝试获取考试信息
      const examResponse = await api.get(`/exams/${examId}`);
      const examInfo = examResponse.data.data;

      if (!examInfo) {
        setError('考试不存在');
        setLoading(false);
        return;
      }

      // 尝试开始或恢复考试会话
      let sessionResponse;
      try {
        sessionResponse = await api.post(`/exam-sessions/${examId}/start`);
      } catch (sessionError: any) {
        // 如果开始会话失败，可能是API还不支持，使用模拟数据
        console.log('开始会话失败，使用模拟模式:', sessionError);
        
        // 模拟会话数据
        const mockSession: SessionData = {
          id: `mock-session-${Date.now()}`,
          attemptNumber: 1,
          startedAt: new Date().toISOString(),
          remainingTime: examInfo.duration * 60 // 转换为秒
        };

        setSessionData(mockSession);
        setExamData({
          ...examInfo,
          totalPoints: examInfo.totalScore || examInfo.totalPoints || 100,
          questions: examInfo.questions || []
        });
        setTimeLeft(mockSession.remainingTime);

        // 初始化答案对象
        const initialAnswers: Record<string, any> = {};
        (examInfo.questions || []).forEach((q: Question) => {
          initialAnswers[q.id] = null;
        });
        setAnswers(initialAnswers);
        setLoading(false);
        return;
      }

      const { session, exam } = sessionResponse.data.data;

      setSessionData(session);
      setExamData({
        ...exam,
        totalPoints: exam.totalScore || exam.totalPoints || 100
      });
      setTimeLeft(session.remainingTime);

      // 初始化答案对象
      const initialAnswers: Record<string, any> = {};
      exam.questions.forEach((q: Question) => {
        initialAnswers[q.id] = null;
      });
      setAnswers(initialAnswers);

    } catch (error: any) {
      console.error('加载考试失败:', error);
      setError(error.response?.data?.message || '加载考试失败');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerChange = (questionId: string, answer: any) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
  };

  const handleMarkForReview = (questionId: string) => {
    setMarkedForReview(prev => {
      const newSet = new Set(prev);
      if (newSet.has(questionId)) {
        newSet.delete(questionId);
      } else {
        newSet.add(questionId);
      }
      return newSet;
    });
  };

  const submitAnswer = async (questionId: string) => {
    try {
      await api.post(`/exam-sessions/${sessionData?.id}/answers`, {
        questionId,
        answer: answers[questionId]
      });
    } catch (error) {
      console.error('提交答案失败:', error);
    }
  };

  const handleQuestionNavigation = (direction: 'prev' | 'next') => {
    if (!examData) return;

    const currentQuestion = examData.questions[currentQuestionIndex];
    // 提交当前题目的答案
    submitAnswer(currentQuestion.id);

    if (direction === 'prev' && currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    } else if (direction === 'next' && currentQuestionIndex < examData.questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    }
  };

  const handleSubmitExam = async () => {
    if (!sessionData || !examData || submitting) return;

    setSubmitting(true);
    
    try {
      // 提交所有未提交的答案
      for (const question of examData.questions) {
        if (answers[question.id] !== null && answers[question.id] !== undefined) {
          await submitAnswer(question.id);
        }
      }

      // 如果是模拟会话，直接显示结果
      if (sessionData.id.startsWith('mock-')) {
        // 计算本地得分
        let score = 0;
        const questionCount = examData.questions.length;
        const answeredCount = Object.values(answers).filter(a => a !== null && a !== undefined && a !== '').length;
        
        toast.success(`考试提交成功！您完成了 ${answeredCount}/${questionCount} 道题`);
        navigate('/student/exams');
        return;
      }

      await api.post(`/exam-sessions/${sessionData.id}/submit`);
      toast.success('考试提交成功');
      navigate(`/student/exam-results/${sessionData.id}`);
    } catch (error: any) {
      console.error('提交失败:', error);
      toast.error(error.response?.data?.message || '提交失败，请重试');
    } finally {
      setSubmitting(false);
    }
  };

  const formatTime = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const getQuestionStatus = (questionId: string) => {
    const answer = answers[questionId];
    const isMarked = markedForReview.has(questionId);

    if (isMarked) return 'marked';
    if (answer !== null && answer !== undefined && answer !== '') return 'answered';
    return 'not-answered';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">正在加载考试...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md mx-auto p-6">
          <AlertTriangle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">加载失败</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => navigate('/student/exams')}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
          >
            返回考试列表
          </button>
        </div>
      </div>
    );
  }

  if (!examData || !sessionData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md mx-auto p-6">
          <AlertTriangle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">考试加载失败</h2>
          <p className="text-gray-600 mb-4">无法加载考试数据，请返回重试</p>
          <button
            onClick={() => navigate('/student/exams')}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
          >
            返回考试列表
          </button>
        </div>
      </div>
    );
  }

  const currentQuestion = examData.questions[currentQuestionIndex];
  const progress = ((currentQuestionIndex + 1) / examData.questions.length) * 100;

  // 考试主内容
  const examContent = (
    <div className="min-h-screen bg-gray-50">
      {/* 头部信息栏 */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <h1 className="text-lg font-semibold text-gray-900">{examData.title}</h1>
              <span className="text-sm text-gray-500">
                第 {currentQuestionIndex + 1} / {examData.questions.length} 题
              </span>
            </div>

            <div className="flex items-center space-x-4">
              {/* 倒计时 */}
              <div className={`flex items-center space-x-1 ${timeLeft < 300 ? 'text-red-600' : 'text-gray-600'}`}>
                <Clock className="w-4 h-4" />
                <span className="font-mono text-sm">
                  {formatTime(timeLeft)}
                </span>
              </div>

              {/* 提交按钮 */}
              <button
                onClick={() => {
                  if (confirm('确定要提交考试吗？提交后无法修改答案。')) {
                    handleSubmitExam();
                  }
                }}
                disabled={submitting}
                className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50 text-sm"
              >
                <Send className="w-4 h-4 inline mr-1" />
                {submitting ? '提交中...' : '提交考试'}
              </button>
            </div>
          </div>

          {/* 进度条 */}
          <div className="pb-4">
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* 主要内容区 */}
          <div className="lg:col-span-3">
            <div className="bg-white shadow-sm rounded-lg p-6">
              {/* 题目内容 */}
              <div className="mb-6">
                  <div className="flex items-start justify-between mb-4">
                  <h2 className="text-xl font-semibold text-gray-900">
                    {currentQuestion.title}
                  </h2>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-500">{currentQuestion.points || 10} 分</span>
                    <button
                      onClick={() => handleMarkForReview(currentQuestion.id)}
                      className={`flex items-center space-x-1 px-3 py-1 rounded-lg text-sm ${
                        markedForReview.has(currentQuestion.id)
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      <Flag className="w-4 h-4" />
                      <span>标记</span>
                    </button>
                  </div>
                </div>

                {currentQuestion.content && (
                  <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                    <p className="text-gray-700">{currentQuestion.content}</p>
                  </div>
                )}

                {/* 题目答案区域 */}
                <div className="space-y-4">
                  {currentQuestion.type === 'single_choice' && (
                    <div className="space-y-2">
                      {currentQuestion.options?.map((option, index) => (
                        <label key={index} className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                          <input
                            type="radio"
                            name={`question-${currentQuestion.id}`}
                            value={index}
                            checked={answers[currentQuestion.id] === index}
                            onChange={(e) => handleAnswerChange(currentQuestion.id, parseInt(e.target.value))}
                            className="text-blue-600 focus:ring-blue-500"
                          />
                          <span className="text-gray-700">{String.fromCharCode(65 + index)}. {option}</span>
                        </label>
                      ))}
                    </div>
                  )}

                  {currentQuestion.type === 'multiple_choice' && (
                    <div className="space-y-2">
                      {currentQuestion.options?.map((option, index) => (
                        <label key={index} className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={answers[currentQuestion.id]?.includes(index) || false}
                            onChange={(e) => {
                              const currentAnswers = answers[currentQuestion.id] || [];
                              let newAnswers;
                              if (currentAnswers.includes(index)) {
                                newAnswers = currentAnswers.filter((a: number) => a !== index);
                              } else {
                                newAnswers = [...currentAnswers, index];
                              }
                              handleAnswerChange(currentQuestion.id, newAnswers);
                            }}
                            className="text-blue-600 focus:ring-blue-500"
                          />
                          <span className="text-gray-700">{String.fromCharCode(65 + index)}. {option}</span>
                        </label>
                      ))}
                    </div>
                  )}

                  {currentQuestion.type === 'true_false' && (
                    <div className="space-y-2">
                      <label className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                        <input
                          type="radio"
                          name={`question-${currentQuestion.id}`}
                          value="true"
                          checked={answers[currentQuestion.id] === true}
                          onChange={(e) => handleAnswerChange(currentQuestion.id, true)}
                          className="text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-gray-700">正确</span>
                      </label>
                      <label className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                        <input
                          type="radio"
                          name={`question-${currentQuestion.id}`}
                          value="false"
                          checked={answers[currentQuestion.id] === false}
                          onChange={(e) => handleAnswerChange(currentQuestion.id, false)}
                          className="text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-gray-700">错误</span>
                      </label>
                    </div>
                  )}

                  {(currentQuestion.type === 'short_answer' || currentQuestion.type === 'essay' || currentQuestion.type === 'code') && (
                    <textarea
                      value={answers[currentQuestion.id] || ''}
                      onChange={(e) => handleAnswerChange(currentQuestion.id, e.target.value)}
                      placeholder={currentQuestion.type === 'code' ? '请输入代码...' : '请输入答案...'}
                      rows={currentQuestion.type === 'essay' ? 8 : currentQuestion.type === 'code' ? 12 : 4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono"
                    />
                  )}
                </div>
              </div>

              {/* 导航按钮 */}
              <div className="flex items-center justify-between pt-6 border-t">
                <button
                  onClick={() => handleQuestionNavigation('prev')}
                  disabled={currentQuestionIndex === 0}
                  className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-4 h-4" />
                  <span>上一题</span>
                </button>

                <div className="text-sm text-gray-600">
                  {currentQuestionIndex + 1} / {examData.questions.length}
                </div>

                <button
                  onClick={() => handleQuestionNavigation('next')}
                  disabled={currentQuestionIndex === examData.questions.length - 1}
                  className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span>下一题</span>
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          {/* 题目导航面板 */}
          <div className="lg:col-span-1">
            <div className="bg-white shadow-sm rounded-lg p-4 sticky top-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">题目导航</h3>

              <div className="grid grid-cols-5 gap-2">
                {examData.questions.map((question, index) => {
                  const status = getQuestionStatus(question.id);
                  return (
                    <button
                      key={question.id}
                      onClick={() => {
                        // 先提交当前题目答案
                        const currentQuestion = examData.questions[currentQuestionIndex];
                        submitAnswer(currentQuestion.id);
                        setCurrentQuestionIndex(index);
                      }}
                      className={`w-10 h-10 rounded-lg border-2 text-sm font-medium transition-colors ${
                        index === currentQuestionIndex
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : status === 'answered'
                          ? 'border-green-500 bg-green-50 text-green-700'
                          : status === 'marked'
                          ? 'border-yellow-500 bg-yellow-50 text-yellow-700'
                          : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                      }`}
                    >
                      {index + 1}
                    </button>
                  );
                })}
              </div>

              {/* 图例 */}
              <div className="mt-4 space-y-2 text-xs text-gray-600">
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 border-2 border-green-500 bg-green-50 rounded"></div>
                  <span>已答题</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 border-2 border-gray-300 bg-white rounded"></div>
                  <span>未答题</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 border-2 border-yellow-500 bg-yellow-50 rounded"></div>
                  <span>标记复查</span>
                </div>
              </div>

              {/* 考试信息 */}
              <div className="mt-6 pt-4 border-t">
                <h4 className="text-sm font-medium text-gray-900 mb-2">考试信息</h4>
                <div className="space-y-1 text-sm text-gray-600">
                  <div>总题数: {examData.questions.length}</div>
                  <div>总分: {examData.totalPoints}</div>
                  <div>答题进度: {Object.values(answers).filter(a => a !== null && a !== undefined && a !== '').length} / {examData.questions.length}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return examContent;
};

export default ExamTaking;
